import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, ProgressBar, Row, Table } from '../common/LegacyUi';
import { CheckCircle2, Copy, Percent, TrendingUp, Trophy } from 'lucide-react';
import { getSellerDashboard } from '../../services/api';

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
const formatMonth = (value) => new Date(`${value}T00:00:00`).toLocaleDateString('es-PA', {
  month: 'short',
  year: 'numeric',
});

const SellerDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const response = await getSellerDashboard();
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'No se pudo cargar tu dashboard de vendedor.');
      }
    };
    loadDashboard();
  }, []);

  if (error) return <Container className="py-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!data) return <Container className="py-5">Cargando...</Container>;

  const dividend = data.dividend_program || {};
  const quarter = dividend.current_quarter || {};
  const quarterMonths = quarter.months || [];
  const progressPercent = Math.min(
    100,
    Math.round(((quarter.qualified_months || 0) / (dividend.streak_months_required || 3)) * 100)
  );
  const statusLabel = {
    earned: 'Ganado',
    on_track: 'Calificado',
    in_progress: 'En progreso',
    not_qualified: 'Pendiente',
  }[quarter.payout_status] || 'Pendiente';

  return (
    <Container className="py-5">
      <div className="mb-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <Badge bg="dark" className="mb-2">Seller account</Badge>
            <h1 className="mb-1">Dashboard de Vendedor</h1>
            <p className="text-muted mb-0">
              Hola {data.seller.display_name || data.seller.username}. Aqui puedes ver tus ventas, comisiones y progreso de dividendos.
            </p>
          </div>
          <Badge bg={quarter.qualified ? 'success' : 'secondary'} className="px-3 py-2">
            Dividendos: {statusLabel}
          </Badge>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="text-muted">Tu link de partner</div>
            <strong className="d-block text-break">{data.seller.affiliate_link}</strong>
          </div>
          <Button variant="dark" onClick={() => navigator.clipboard.writeText(data.seller.affiliate_link)}>
            <Copy size={16} /> Copiar
          </Button>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col md={4}><Card><Card.Body><div className="text-muted">Ventas</div><h3>{data.summary.sales_count}</h3></Card.Body></Card></Col>
        <Col md={4}><Card><Card.Body><div className="text-muted">Total vendido</div><h3>{formatMoney(data.summary.total_sales)}</h3></Card.Body></Card></Col>
        <Col md={4}><Card><Card.Body><div className="text-muted">Comisiones 10%</div><h3>{formatMoney(data.summary.total_commissions)}</h3></Card.Body></Card></Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col lg={5}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Percent size={18} />
                <h5 className="mb-0">Programa de comisiones</h5>
              </div>
              <p className="text-muted mb-3">
                Recibes {Number(dividend.commission_rate || 10).toFixed(0)}% por cada venta atribuida a tu link.
              </p>
              <div className="d-flex align-items-center gap-2">
                <Trophy size={18} />
                <strong>{Number(dividend.dividend_share_rate || 1).toFixed(0)}% del share FOTH</strong>
              </div>
              <p className="text-muted mb-0">
                Se habilita trimestralmente si registras al menos {dividend.minimum_monthly_sales || 20} negocios por mes durante {dividend.streak_months_required || 3} meses seguidos.
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <TrendingUp size={18} />
                    <h5 className="mb-0">Progreso trimestral</h5>
                  </div>
                  <div className="text-muted small">
                    {quarter.start_month && quarter.end_month
                      ? `${formatMonth(quarter.start_month)} - ${formatMonth(quarter.end_month)}`
                      : 'Trimestre actual'}
                  </div>
                </div>
                <Badge bg={quarter.qualified ? 'success' : 'secondary'}>
                  {quarter.qualified_months || 0}/{dividend.streak_months_required || 3} meses
                </Badge>
              </div>
              <ProgressBar now={progressPercent} variant={quarter.qualified ? 'success' : 'dark'} className="mb-3" />
              <Row className="g-2">
                {quarterMonths.map(month => (
                  <Col md={4} key={month.month}>
                    <div className="border rounded p-3 h-100">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <strong>{formatMonth(month.month)}</strong>
                        {month.qualified && <CheckCircle2 size={18} className="text-success" />}
                      </div>
                      <div className="display-6 fs-3">{month.sales_count}</div>
                      <div className="text-muted small">negocios vendidos</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <h5>Mis ventas</h5>
          <Table hover responsive className="align-middle">
            <thead><tr><th>Fecha</th><th>Cliente</th><th>Plan</th><th>Venta</th><th>Comision</th></tr></thead>
            <tbody>
              {data.sales.length === 0 && <tr><td colSpan="5" className="text-center">Aun no hay ventas registradas.</td></tr>}
              {data.sales.map(sale => (
                <tr key={sale.id}>
                  <td>{new Date(sale.created_at).toLocaleDateString()}</td>
                  <td>{sale.buyer_email || sale.buyer_username}</td>
                  <td>{sale.plan_name}</td>
                  <td>{formatMoney(sale.gross_amount)}</td>
                  <td>{formatMoney(sale.commission_amount)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SellerDashboard;
